import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ImageModule } from '@df/module-image/image.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { RatioOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { YoutubePlayerComponent } from '@df/ui/display/media/youtube-player.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { defaultImage } from 'projects/app-default/src/app/icms.config';

export namespace NPageComponentEditorialComponent {
    export interface IComponentData {
        variant: 'image' | 'video';
        image?: string;
        videoId?: string;
        videoProvider?: 'youtube' | 'vimeo';
        videoBackgroundMode?: boolean;
        ratio?: string;
        title?: string;
        description?: string;
    }
}
@Component({
    selector: 'editorial-component',
    templateUrl: './editorial.html',
    standalone: true,
    imports: [GridModule, NgClass, NgIf, ImageModule, YoutubePlayerComponent, VimeoPlayerComponent, ICmsClientModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentEditorialComponent>({
    id: 'editorial-component',
    name: 'Editorial Component',
    fields: [
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'variant',
            title: 'Variant',
            default: 'image',
            options: [
                {
                    key: 'image',
                    title: 'Image'
                },
                {
                    key: 'video',
                    title: 'Video'
                }
            ]
        },
        {
            key: 'image',
            title: 'Image Upload',
            type: NICmsContentField.EFieldType.image,
            default: defaultImage,
            canShow: (data: NPageComponentEditorialComponent.IComponentData) => data?.variant === 'image'
        },
        <NICmsContentField.IChoiceField>{
            key: 'ratio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Ratio',
            options: RatioOptions,
            default: 'ratio-1-1'
        },
        {
            key: 'videoId',
            type: NICmsContentField.EFieldType.text,
            title: 'ID or URL',
            description: 'Video ID or URL',
            default: '',
            canShow: (data: NPageComponentEditorialComponent.IComponentData) => data?.variant === 'video'
        },
        <NICmsContentField.IChoiceField>{
            key: 'videoProvider',
            type: NICmsContentField.EFieldType.choice,
            title: 'Provider',
            options: [
                ...UndefinedOption,
                {
                    key: 'youtube',
                    title: 'YouTube'
                },
                {
                    key: 'vimeo',
                    title: 'Vimeo'
                }
            ],
            description: 'Choose a video provider',
            canShow: (data: NPageComponentEditorialComponent.IComponentData) => data?.variant === 'video'
        },
        {
            key: 'videoBackgroundMode',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Background Mode',
            default: true,
            canShow: (data: NPageComponentEditorialComponent.IComponentData) => data?.variant === 'video'
        },
        {
            key: 'videoAutoplay',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Autoplay',
            default: true,
            canShow: (data: NPageComponentEditorialComponent.IComponentData) => !data?.videoBackgroundMode && data?.variant === 'video'
        },
        {
            key: 'title',
            title: 'Title',
            default: 'Title',
            type: NICmsContentField.EFieldType.text
        },
        {
            key: 'description',
            title: 'Description',
            default:
                'Lorem ipsum dolor sit amet consectetur adipisicing elit. Nemo fugit nostrum dolorum iusto amet aut nihil repellendus sint reprehenderit ipsam exercitationem aspernatur earum optio similique iste sed, accusamus illo natus?',
            type: NICmsContentField.EFieldType.textarea
        },
        {
            key: 'swapOrder',
            title: 'Swap the order',
            description: 'Swaps the order of the image/video and the text',
            type: NICmsContentField.EFieldType.toggle
        },
        NICmsContentComponent.backgroundField
    ]
})
export class PageComponentEditorialComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component: NPageComponentEditorialComponent.IComponentData;
}
